/**
 * Properties API Service
 * Property management related API calls
 */

import { api } from '@/shared/api/apiClient';
import { API_ROUTES, buildUrl } from '@/config/apiRoutes';
import type { PaginatedResponse, SearchParams } from '@/shared/api/types';
import type { Property, PropertyListParams } from '@/types/entities';

// Re-export types for convenience
export type { Property };

// Property Details from property-scraping/property-details endpoint
export interface PropertyDetails {
    listing_credit: string;
    basic_info: {
        address: string;
        status: string;
        days_on_market: number;
        price: string;
        beds: string;
        baths: string;
        sqft: string;
    };
    description: string;
    specs: {
        mls_number: string;
        taxes: string;
        property_type: string;
        year_built: string;
        style: string;
        school_district: string;
        county: string;
    };
    features: {
        bathroom_details: string[];
        interior_features: string[];
        exterior_features: string[];
        lot_info: string[];
        property_features: string[];
        heating_cooling: string[];
        basement_features: string[];
        utility_features: string[];
    };
    stories: string;
    living_area: string;
    gallery: string[];
    created_at?: string;
    updated_at?: string;
}

// Extended params with SearchParams
export interface PropertiesListParams extends SearchParams, PropertyListParams {}

// ===========================================
// Properties Service
// ===========================================
export const propertiesApi = {
    /**
     * Get paginated properties list
     */
    getProperties: async (params?: PropertiesListParams): Promise<PaginatedResponse<Property>> => {
        // Spread into a plain object: buildUrl wants an indexable record,
        // which an interface type is not.
        const url = buildUrl(API_ROUTES.admin.properties.base, { ...params });
        return api.get<PaginatedResponse<Property>>(url);
    },

    /**
     * Get property details
     */
    getPropertyDetails: async (listingId: string): Promise<PropertyDetails> => {
        return api.get<PropertyDetails>(API_ROUTES.public.properties.getById(listingId));
    },

    /**
     * Search properties
     */
    searchProperties: async (
        query: string,
        page: number = 1,
        limit: number = 10
    ): Promise<PaginatedResponse<Property>> => {
        const url = buildUrl(API_ROUTES.admin.properties.base, { search: query, page, limit });
        return api.get<PaginatedResponse<Property>>(url);
    },

    /**
     * Get properties for sale (public)
     */
    getForSale: async (params?: PropertiesListParams): Promise<PaginatedResponse<Property>> => {
        const url = buildUrl(API_ROUTES.public.properties.forSale, { ...params });
        return api.get<PaginatedResponse<Property>>(url);
    },

    /**
     * Create new property
     */
    createProperty: async (data: Partial<Property>): Promise<Property> => {
        return api.post<Property>(API_ROUTES.admin.properties.create, data);
    },
};

export default propertiesApi;
